/**
 * APP CONTEXT v4.0 - SPLIT ARCHITECTURE
 *
 * ANTES (v3.0): 1 contexto monolítico con ~350 líneas de lógica
 *   Problema: Cuando stock cambia → todos los consumidores re-renderizan
 *   Problema: syncBackground en deps → cadena de re-creación en cascada
 *
 * AHORA (v4.0): 4 contextos independientes
 *   AppContext     → components, recipes, isInitialized, helpers compartidos
 *   StockContext   → stock + CRUD
 *   PlannerContext → mealPlan + operaciones (consume StockContext internamente)
 *   ShoppingContext→ shoppingLists (consume Stock + Planner internamente)
 *
 * COMPATIBILIDAD: useApp() sigue funcionando igual en todas las páginas.
 * No hay breaking changes en StockPage, PlannerPage ni ShoppingPage.
 *
 * ÁRBOL DE PROVIDERS:
 *   AppProvider
 *     └── StockProvider
 *           └── PlannerProvider
 *                 └── ShoppingProvider
 *                       └── TutorialProvider
 *                             └── children
 */

import {
  createContext, useContext, useState, useEffect,
  useCallback, useMemo, memo
} from 'react';
import { useFirebase } from './FirebaseContext';
import { TutorialProvider } from './TutorialContext';
import {
  stockStorage,
  mealPlanStorage,
  shoppingListsStorage,
  initializeStorage
} from '../models/storage';
import {
  calculateMealIngredients
} from '../models/validators';
import { MEAL_STATUS, MEAL_SOURCE_TYPE, normalizeCategory } from '../models/types';
import { PRODUCT_CATALOG } from '../data/productCatalog';
import simpleComponentService from '../services/simpleComponentService';
import simpleRecipeService from '../services/simpleRecipeService';

// ─────────────────────────────────────────────
// HELPERS PUROS (fuera de cualquier componente)
// ─────────────────────────────────────────────

const normalizeToBase = (qty, un) => {
  const u = (un || 'unidad').toLowerCase().trim();
  const q = parseFloat(qty) || 0;
  if (u === 'kg') return { q: q * 1000, u: 'g' };
  if (u === 'l' || u === 'litro' || u === 'litros') return { q: q * 1000, u: 'ml' };
  if (u === 'gr') return { q, u: 'g' };
  if (u === 'cc') return { q, u: 'ml' };
  return { q, u };
};

const normalizeNameStr = (name) =>
  name.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ');

const isFuzzyMatch = (stockName, recipeName) => {
  if (stockName === recipeName) return true;
  if (stockName + 's' === recipeName || stockName === recipeName + 's') return true;
  try {
    const escaped = recipeName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(stockName);
  } catch {
    return stockName.includes(recipeName);
  }
};

// ─────────────────────────────────────────────
// 1. APP CONTEXT — components, recipes, helpers
// ─────────────────────────────────────────────

const AppContext = createContext();

export const useAppBase = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppBase debe usarse dentro de AppProvider');
  return ctx;
};

const AppProviderBase = ({ children }) => {
  const { household } = useFirebase();

  const [components, setComponents] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inicialización del storage local
  useEffect(() => {
    try {
      initializeStorage();
      setIsInitialized(true);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  // Suscripción a componentes y recetas por hogar
  useEffect(() => {
    if (!household?.id) return;

    const unsubComponents = simpleComponentService.subscribeToComponents(
      household.id,
      (data) => setComponents(data)
    );
    const unsubRecipes = simpleRecipeService.subscribeToRecipes(
      household.id,
      (data) => setRecipes(data)
    );

    return () => {
      unsubComponents();
      unsubRecipes();
    };
  }, [household?.id]);

  // Helpers que dependen de components y recipes
  const getRecipeById = useCallback(
    (id) => recipes.find(r => r.id === id) || null,
    [recipes]
  );

  const getComponentById = useCallback(
    (id) => components.find(c => c.id === id) || null,
    [components]
  );

  const getMealIngredients = useCallback((mealData) => {
    if (!mealData || mealData.type === MEAL_SOURCE_TYPE.MANUAL) return [];

    if (mealData.type === MEAL_SOURCE_TYPE.COMBO) {
      return calculateMealIngredients(mealData, components);
    }

    if (mealData.type === MEAL_SOURCE_TYPE.RECIPE && mealData.recipeId) {
      const recipe = recipes.find(r => r.id === mealData.recipeId);
      if (!recipe) return [];

      const tempComponents = [];
      if (recipe.main) {
        tempComponents.push({ id: recipe.main.componentId, servings: recipe.main.servings || 1 });
      }
      (recipe.sides || []).forEach(s => {
        tempComponents.push({ id: s.componentId, servings: s.servings || 1 });
      });

      return calculateMealIngredients({ ...mealData, components: tempComponents }, components);
    }

    return [];
  }, [components, recipes]);

  const value = useMemo(() => ({
    components,
    recipes,
    isInitialized,
    loading,
    error,
    getRecipeById,
    getComponentById,
    getMealIngredients,
    // Exponer helpers de normalización para contextos hijos
    _normalizeToBase: normalizeToBase,
    _normalizeNameStr: normalizeNameStr,
    _isFuzzyMatch: isFuzzyMatch,
  }), [
    components, recipes, isInitialized, loading, error,
    getRecipeById, getComponentById, getMealIngredients,
  ]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// ─────────────────────────────────────────────
// 2. STOCK CONTEXT
// ─────────────────────────────────────────────

const StockContext = createContext();

export const useStock = () => {
  const ctx = useContext(StockContext);
  if (!ctx) throw new Error('useStock debe usarse dentro de StockProvider');
  return ctx;
};

const StockProvider = ({ children }) => {
  const { syncToFirebase, syncEnabled, stockFromFirebase } = useFirebase();
  const { isInitialized } = useAppBase();

  // Estado inicial: localStorage como fallback mientras Firebase no responde.
  // Cuando Firebase responde (stockFromFirebase != null), se usa ese valor.
  const [stock, setStock] = useState(() => stockStorage.getAll() || []);

  const syncBackground = useCallback((dataToSync, options = {}) => {
    if (syncEnabled && isInitialized) {
      syncToFirebase(dataToSync, options).catch(err =>
        console.warn('\u26a0\ufe0f Stock sync warning:', err)
      );
    }
  }, [syncEnabled, isInitialized, syncToFirebase]);

  // Firebase → estado React (reemplaza CustomEvents + localStorage manual)
  // null = todavía no llegó la primera respuesta
  // [] o array = Firebase respondió, usar ese valor como fuente de verdad
  useEffect(() => {
    if (stockFromFirebase === null) return;
    stockStorage.save(stockFromFirebase);
    setStock(stockFromFirebase);
  }, [stockFromFirebase]);

  const addStockItem = useCallback(async (item) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const newItem = stockStorage.add(item);
      const updatedList = stockStorage.getAll();
      setStock(updatedList);
      syncBackground({ stock: updatedList }, { forceFullSync: true });
      return { success: true, data: newItem, fullList: updatedList };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const updateStockItem = useCallback(async (id, updates) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const updated = stockStorage.update(id, updates);
      const updatedList = stockStorage.getAll();
      setStock(updatedList);
      syncBackground({ stock: updatedList }, { forceFullSync: true });
      return { success: true, data: updated, fullList: updatedList };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const deleteStockItem = useCallback(async (id) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      stockStorage.delete(id);
      const updatedList = stockStorage.getAll();
      setStock(updatedList);
      syncBackground({ stock: updatedList }, { forceFullSync: true });
      return { success: true, fullList: updatedList };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  // Exponer updateStockBulk para uso interno de PlannerContext (markMealAsCooked)
  const updateStockBulk = useCallback((newStockArray) => {
    stockStorage.save(newStockArray);
    setStock(newStockArray);
    syncBackground({ stock: newStockArray }, { forceFullSync: true });
  }, [syncBackground]);

  const value = useMemo(() => ({
    stock,
    addStockItem,
    updateStockItem,
    deleteStockItem,
    // interno, usado por PlannerContext y ShoppingContext
    _updateStockBulk: updateStockBulk,
    _syncBackground: syncBackground,
  }), [
    stock,
    addStockItem, updateStockItem, deleteStockItem,
    updateStockBulk, syncBackground,
  ]);

  return <StockContext.Provider value={value}>{children}</StockContext.Provider>;
};

// ─────────────────────────────────────────────
// 3. PLANNER CONTEXT
// ─────────────────────────────────────────────

const PlannerContext = createContext();

export const usePlanner = () => {
  const ctx = useContext(PlannerContext);
  if (!ctx) throw new Error('usePlanner debe usarse dentro de PlannerProvider');
  return ctx;
};

const PlannerProvider = ({ children }) => {
  const { syncEnabled, syncToFirebase, mealPlanFromFirebase } = useFirebase();
  const { isInitialized, getMealIngredients } = useAppBase();
  const { stock, _updateStockBulk } = useStock();

  const [mealPlan, setMealPlan] = useState(() => mealPlanStorage.getAll() || []);

  // syncBackground propio de Planner — independiente del StockContext
  // Cambios en stock NO recrean esta función porque no depende de stockCtx
  const syncBackground = useCallback((dataToSync, options = {}) => {
    if (syncEnabled && isInitialized) {
      syncToFirebase(dataToSync, options).catch(err =>
        console.warn('⚠️ MealPlan sync warning:', err)
      );
    }
  }, [syncEnabled, isInitialized, syncToFirebase]);

  // Firebase → estado React (reemplaza CustomEvents + localStorage manual)
  useEffect(() => {
    if (mealPlanFromFirebase === null) return;
    mealPlanStorage.save(mealPlanFromFirebase);
    setMealPlan(mealPlanFromFirebase);
  }, [mealPlanFromFirebase]);

  const setMeal = useCallback(async (date, mealType, mealData) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      mealPlanStorage.set(date, mealType, mealData);
      const updatedList = mealPlanStorage.getAll();
      setMealPlan(updatedList);
      syncBackground({ mealPlan: updatedList }, { forceFullSync: true });
      return { success: true, fullList: updatedList };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const removeMeal = useCallback(async (date, mealType) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      mealPlanStorage.remove(date, mealType);
      const updatedList = mealPlanStorage.getAll();
      setMealPlan(updatedList);
      syncBackground({ mealPlan: updatedList }, { forceFullSync: true });
      return { success: true, fullList: updatedList };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const markMealAsCooked = useCallback(async (date, mealType) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const meal = mealPlan.find(m => m.date === date);
      if (!meal || !meal[mealType]) throw new Error('Comida no planificada');

      const mealData = meal[mealType];

      // Comidas manuales: solo marcar, no descontar stock
      if (mealData.type === MEAL_SOURCE_TYPE.MANUAL) {
        mealPlanStorage.markAsCooked(date, mealType);
        const updatedMealPlan = mealPlanStorage.getAll();
        setMealPlan(updatedMealPlan);
        return { success: true, fullStock: stock, fullMealPlan: updatedMealPlan };
      }

      const ingredients = getMealIngredients(mealData);
      let currentStock = [...stockStorage.getAll()];

      // Validar stock disponible
      const missingIngredients = [];
      ingredients.forEach(ing => {
        const neededBase = normalizeToBase(ing.quantity, ing.unit);
        let availableBase = 0;
        const ingNameNorm = normalizeNameStr(ing.name);

        currentStock
          .filter(s => isFuzzyMatch(normalizeNameStr(s.name), ingNameNorm))
          .forEach(s => {
            const stockBase = normalizeToBase(s.quantity, s.unit);
            if (stockBase.u === neededBase.u) availableBase += stockBase.q;
          });

        if (availableBase < neededBase.q) missingIngredients.push(ing);
      });

      if (missingIngredients.length > 0) {
        return { success: false, error: 'Stock insuficiente', missingIngredients };
      }

      // Descontar stock
      ingredients.forEach(ing => {
        const neededBase = normalizeToBase(ing.quantity, ing.unit);
        let remaining = neededBase.q;
        const ingNameNorm = normalizeNameStr(ing.name);

        for (let i = 0; i < currentStock.length; i++) {
          if (remaining <= 0) break;
          const s = currentStock[i];
          if (!isFuzzyMatch(normalizeNameStr(s.name), ingNameNorm)) continue;

          const stockBase = normalizeToBase(s.quantity, s.unit);
          if (stockBase.u !== neededBase.u) continue;

          if (stockBase.q <= remaining) {
            remaining -= stockBase.q;
            s.quantity = 0;
          } else {
            const newBaseQty = stockBase.q - remaining;
            const origUnit = (s.unit || '').toLowerCase().trim();
            s.quantity = (origUnit === 'kg' || origUnit === 'l' || origUnit === 'litro')
              ? newBaseQty / 1000
              : newBaseQty;
            remaining = 0;
          }
        }
      });

      const finalStock = currentStock.filter(s => parseFloat(s.quantity) > 0.01);
      _updateStockBulk(finalStock);

      mealPlanStorage.markAsCooked(date, mealType);
      const updatedMealPlan = mealPlanStorage.getAll();
      setMealPlan(updatedMealPlan);

      return { success: true, fullStock: finalStock, fullMealPlan: updatedMealPlan };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, mealPlan, stock, getMealIngredients, _updateStockBulk]);

  const unmarkMealAsCooked = useCallback(async (date, mealType) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      mealPlanStorage.unmarkAsCooked(date, mealType);
      const updatedMealPlan = mealPlanStorage.getAll();
      setMealPlan(updatedMealPlan);
      return { success: true, fullMealPlan: updatedMealPlan };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized]);

  const cancelMeal = useCallback(async (date, mealType) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      mealPlanStorage.markAsCancelled(date, mealType);
      const updatedMealPlan = mealPlanStorage.getAll();
      setMealPlan(updatedMealPlan);
      return { success: true, fullMealPlan: updatedMealPlan };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized]);

  const getMealByDate = useCallback(
    (date) => mealPlan.find(m => m.date === date) || null,
    [mealPlan]
  );

  const getMealStatus = useCallback(
    (date, mealType) => mealPlanStorage.getMealStatus(date, mealType),
    []
  );

  const value = useMemo(() => ({
    mealPlan,
    setMeal,
    removeMeal,
    markMealAsCooked,
    unmarkMealAsCooked,
    cancelMeal,
    getMealByDate,
    getMealStatus,
    // interno, usado por ShoppingContext
    _mealPlan: mealPlan,
  }), [
    mealPlan,
    setMeal, removeMeal, markMealAsCooked, unmarkMealAsCooked, cancelMeal,
    getMealByDate, getMealStatus,
  ]);

  return <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>;
};

// ─────────────────────────────────────────────
// 4. SHOPPING CONTEXT
// ─────────────────────────────────────────────

const ShoppingContext = createContext();

export const useShopping = () => {
  const ctx = useContext(ShoppingContext);
  if (!ctx) throw new Error('useShopping debe usarse dentro de ShoppingProvider');
  return ctx;
};

const ShoppingProvider = ({ children }) => {
  // 👇 AGREGAMOS user ACÁ 👇
  const { syncEnabled, syncToFirebase, shoppingListsFromFirebase, user } = useFirebase(); 
  const { isInitialized, getMealIngredients } = useAppBase();
  const { stock, _updateStockBulk } = useStock();
  const { _mealPlan } = usePlanner();

  const [shoppingLists, setShoppingLists] = useState(() => shoppingListsStorage.getAll() || []);
  const [activeListId, setActiveListId] = useState(() => {
    const lists = shoppingListsStorage.getAll() || [];
    return lists.length > 0 ? lists[0].id : null;
  });

  // 👇 NUEVO SYNC BACKGROUND INTELIGENTE 👇
  const syncBackground = useCallback((dataToSync, options = {}) => {
    if (syncEnabled && isInitialized && dataToSync.shoppingLists) {
      
      // Inyectamos el autor del cambio en TODAS las listas que estamos sincronizando
      const listasConAutor = dataToSync.shoppingLists.map(lista => ({
        ...lista,
        lastUpdatedBy: user?.uid || 'unknown',
        lastUpdatedByName: user?.displayName || user?.email || 'Alguien'
      }));

      // Mandamos las listas modificadas a Firebase
      syncToFirebase({ shoppingLists: listasConAutor }, options).catch(err =>
        console.warn('⚠️ Shopping sync warning:', err)
      );
    }
  }, [syncEnabled, isInitialized, syncToFirebase, user]);

  // Firebase → estado React (reemplaza CustomEvents + localStorage manual)
  // Preserva activeListId: si la lista activa fue eliminada en otro dispositivo,
  // cae automáticamente a la primera lista disponible.
  useEffect(() => {
    if (shoppingListsFromFirebase === null) return;
    shoppingListsStorage.save(shoppingListsFromFirebase);
    setShoppingLists(shoppingListsFromFirebase);
    if (activeListId && !shoppingListsFromFirebase.find(l => l.id === activeListId)) {
      setActiveListId(shoppingListsFromFirebase.length > 0 ? shoppingListsFromFirebase[0].id : null);
    }
  }, [shoppingListsFromFirebase, activeListId]);

  const createShoppingList = useCallback(async (listData) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const newList = shoppingListsStorage.create(listData);
      const updatedLists = shoppingListsStorage.getAll();
      setShoppingLists(updatedLists);
      setActiveListId(newList.id);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });
      return { success: true, data: newList, fullList: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const updateShoppingList = useCallback(async (listId, updates) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const updated = shoppingListsStorage.update(listId, updates);
      const updatedLists = shoppingListsStorage.getAll();
      setShoppingLists(updatedLists);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });
      return { success: true, data: updated, fullList: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const deleteShoppingList = useCallback(async (listId) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      shoppingListsStorage.delete(listId);
      const lists = shoppingListsStorage.getAll();
      setShoppingLists(lists);
      if (activeListId === listId) {
        setActiveListId(lists.length > 0 ? lists[0].id : null);
      }
      syncBackground({ shoppingLists: lists }, { forceFullSync: true });
      return { success: true, fullList: lists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, activeListId, syncBackground]);

  const addItemToList = useCallback(async (listId, item) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const newItem = shoppingListsStorage.addItem(listId, item);
      const updatedLists = shoppingListsStorage.getAll();
      setShoppingLists(updatedLists);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });
      return { success: true, data: newItem, fullList: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const updateListItem = useCallback(async (listId, itemId, updates) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const updated = shoppingListsStorage.updateItem(listId, itemId, updates);
      const updatedLists = shoppingListsStorage.getAll();
      setShoppingLists(updatedLists);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });
      return { success: true, data: updated, fullList: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const deleteListItem = useCallback(async (listId, itemId) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      shoppingListsStorage.deleteItem(listId, itemId);
      const updatedLists = shoppingListsStorage.getAll();
      setShoppingLists(updatedLists);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });
      return { success: true, fullList: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const toggleListItemPurchased = useCallback(async (listId, itemId) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      shoppingListsStorage.togglePurchased(listId, itemId);
      const updatedLists = shoppingListsStorage.getAll();
      setShoppingLists(updatedLists);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });
      return { success: true, fullList: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, syncBackground]);

  const addListPurchasedToStock = useCallback(async (listId) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const count = shoppingListsStorage.addPurchasedToStock(listId);
      const updatedStock = stockStorage.getAll();
      const updatedLists = shoppingListsStorage.getAll();
      _updateStockBulk(updatedStock);
      setShoppingLists(updatedLists);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });
      return { success: true, count, fullStock: updatedStock, fullLists: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, _updateStockBulk, syncBackground]);

  const generateListFromPlan = useCallback(async (startDate, endDate, listName) => {
    if (!isInitialized) return { success: false, error: 'No inicializado' };
    try {
      const mealsInRange = _mealPlan.filter(m => m.date >= startDate && m.date <= endDate);
      if (mealsInRange.length === 0) {
        return { success: false, error: 'No hay comidas planificadas en ese rango' };
      }

      const neededIngredients = {};

      for (const meal of mealsInRange) {
        for (const mealType of ['breakfast', 'lunch', 'snack', 'dinner']) {
          const mealData = meal[mealType];
          if (!mealData || mealPlanStorage.getMealStatus(meal.date, mealType) !== MEAL_STATUS.PLANNED) continue;
          if (mealData.type === MEAL_SOURCE_TYPE.MANUAL) continue;

          const ingredients = getMealIngredients(mealData);

          ingredients.forEach(ing => {
            const normalizedName = normalizeNameStr(ing.name);
            const normalizedUnit = (ing.unit || 'unidad').toLowerCase().trim();
            const key = `${normalizedName}__${normalizedUnit}`;

            if (!neededIngredients[key]) {
              const catalogMatch = PRODUCT_CATALOG.find(p => {
                const pName = normalizeNameStr(p.name);
                return pName === normalizedName ||
                  pName + 's' === normalizedName ||
                  pName === normalizedName + 's' ||
                  pName === normalizedName.slice(0, -1);
              });
              neededIngredients[key] = {
                name: catalogMatch ? catalogMatch.name : ing.name.trim(),
                unit: ing.unit,
                totalQuantity: 0,
                category: catalogMatch ? catalogMatch.category : normalizeCategory(ing.category || 'otros'),
                normalizedName,
              };
            }
            neededIngredients[key].totalQuantity += parseFloat(ing.quantity);
          });
        }
      }

      const generateId = () => `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const items = [];

      Object.values(neededIngredients).forEach(ingredient => {
        const neededBase = normalizeToBase(ingredient.totalQuantity, ingredient.unit);
        let availableBaseQty = 0;
        let price = null;

        stock
          .filter(s => isFuzzyMatch(normalizeNameStr(s.name), ingredient.normalizedName))
          .forEach(s => {
            const stockBase = normalizeToBase(s.quantity, s.unit);
            if (stockBase.u === neededBase.u) {
              availableBaseQty += stockBase.q;
              if (s.price) price = s.price;
            }
          });

        const missingBaseQty = Math.max(0, neededBase.q - availableBaseQty);
        if (missingBaseQty <= 0.01) return;

        const origU = (ingredient.unit || 'unidad').toLowerCase().trim();
        let finalQty = missingBaseQty;
        let finalUnit = neededBase.u;

        if (origU === 'kg' && missingBaseQty >= 1000) { finalQty = missingBaseQty / 1000; finalUnit = 'kg'; }
        else if ((origU === 'l' || origU === 'litro') && missingBaseQty >= 1000) { finalQty = missingBaseQty / 1000; finalUnit = 'l'; }
        else if (origU === 'kg') finalUnit = 'g';
        else if (origU === 'l' || origU === 'litro') finalUnit = 'ml';

        items.push({
          id: generateId(),
          name: ingredient.name,
          quantity: Math.round(finalQty * 100) / 100,
          unit: finalUnit,
          category: ingredient.category,
          price: price || null,
          purchased: false,
          isManual: false,
        });
      });

      if (items.length === 0) {
        return { success: false, error: 'Ya tenés todos los ingredientes en stock', info: 'No hace falta comprar nada' };
      }

      const newList = shoppingListsStorage.create({
        name: listName || `Lista ${startDate} al ${endDate}`,
        startDate,
        endDate,
        items,
        isAutoGenerated: true,
      });

      const updatedLists = shoppingListsStorage.getAll();
      setShoppingLists(updatedLists);
      setActiveListId(newList.id);
      syncBackground({ shoppingLists: updatedLists }, { forceFullSync: true });

      return { success: true, data: newList, fullList: updatedLists };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [isInitialized, _mealPlan, stock, getMealIngredients, syncBackground]);

  const getListTotalPrice = useCallback(
    (listId) => shoppingListsStorage.getTotalPrice(listId),
    []
  );

  const value = useMemo(() => ({
    shoppingLists,
    activeListId,
    setActiveListId,
    createShoppingList,
    updateShoppingList,
    deleteShoppingList,
    addItemToList,
    updateListItem,
    deleteListItem,
    toggleListItemPurchased,
    addListPurchasedToStock,
    generateListFromPlan,
    getListTotalPrice,
  }), [
    shoppingLists, activeListId,
    createShoppingList, updateShoppingList, deleteShoppingList,
    addItemToList, updateListItem, deleteListItem,
    toggleListItemPurchased, addListPurchasedToStock, generateListFromPlan,
    getListTotalPrice,
  ]);

  return <ShoppingContext.Provider value={value}>{children}</ShoppingContext.Provider>;
};

// ─────────────────────────────────────────────
// ALIAS DE COMPATIBILIDAD — useApp() sigue
// funcionando igual en todas las páginas
// ─────────────────────────────────────────────

/**
 * useApp() — hook de compatibilidad
 *
 * Devuelve la unión de los 4 contextos.
 * StockPage, PlannerPage y ShoppingPage no necesitan cambiar ningún import.
 *
 * A futuro, cada página puede migrar a su hook específico:
 *   StockPage   → useStock()
 *   PlannerPage → usePlanner() + useAppBase()
 *   ShoppingPage→ useShopping()
 */
export const useApp = () => {
  const appBase = useAppBase();
  const stockCtx = useStock();
  const plannerCtx = usePlanner();
  const shoppingCtx = useShopping();

  // useMemo para no recrear el objeto en cada render
  return useMemo(() => ({
    // AppBase
    components: appBase.components,
    recipes: appBase.recipes,
    isInitialized: appBase.isInitialized,
    loading: appBase.loading,
    error: appBase.error,
    getRecipeById: appBase.getRecipeById,
    getComponentById: appBase.getComponentById,
    getMealIngredients: appBase.getMealIngredients,

    // Stock
    stock: stockCtx.stock,
    addStockItem: stockCtx.addStockItem,
    updateStockItem: stockCtx.updateStockItem,
    deleteStockItem: stockCtx.deleteStockItem,

    // Planner
    mealPlan: plannerCtx.mealPlan,
    setMeal: plannerCtx.setMeal,
    removeMeal: plannerCtx.removeMeal,
    markMealAsCooked: plannerCtx.markMealAsCooked,
    unmarkMealAsCooked: plannerCtx.unmarkMealAsCooked,
    cancelMeal: plannerCtx.cancelMeal,
    getMealByDate: plannerCtx.getMealByDate,
    getMealStatus: plannerCtx.getMealStatus,

    // Shopping
    shoppingLists: shoppingCtx.shoppingLists,
    activeListId: shoppingCtx.activeListId,
    setActiveListId: shoppingCtx.setActiveListId,
    createShoppingList: shoppingCtx.createShoppingList,
    updateShoppingList: shoppingCtx.updateShoppingList,
    deleteShoppingList: shoppingCtx.deleteShoppingList,
    addItemToList: shoppingCtx.addItemToList,
    updateListItem: shoppingCtx.updateListItem,
    deleteListItem: shoppingCtx.deleteListItem,
    toggleListItemPurchased: shoppingCtx.toggleListItemPurchased,
    addListPurchasedToStock: shoppingCtx.addListPurchasedToStock,
    generateListFromPlan: shoppingCtx.generateListFromPlan,
    getListTotalPrice: shoppingCtx.getListTotalPrice,

    // Compatibilidad legacy
    loadData: () => {},
  }), [appBase, stockCtx, plannerCtx, shoppingCtx]);
};

// ─────────────────────────────────────────────
// COMPONENTES DE ESTADO
// ─────────────────────────────────────────────

const LoadingState = memo(function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
    </div>
  );
});

const ErrorState = memo(function ErrorState({ error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
      <div className="text-center">
        <h2 className="text-xl font-bold mb-2">Error</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 rounded hover:bg-blue-700 transition active:scale-95"
        >
          Reintentar
        </button>
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────
// EXPORT PÚBLICO — AppProvider orquesta todo
// ─────────────────────────────────────────────

/**
 * AppProvider — reemplaza directamente al AppProvider anterior.
 * El árbol de providers se autogestiona internamente.
 * En main.jsx o App.jsx no cambia nada.
 */
export const AppProvider = ({ children }) => (
  <AppProviderBase>
    <StockProvider>
      <PlannerProvider>
        <ShoppingProvider>
          <TutorialProvider>
            {children}
          </TutorialProvider>
        </ShoppingProvider>
      </PlannerProvider>
    </StockProvider>
  </AppProviderBase>
);